import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { suggestPlaces, type Suggestion } from "../../lib/api/lidoo.functions";
import { countryName, regionName, useI18n } from "../../lib/i18n";

// Type-ahead over coasts, towns and clubs.
//
// The catalogue used to open with thirty-five region chips stacked over nine-
// teen country rows: about half a screen of navigation before the first club,
// and alphabetical, so Albania came first and Sicily's 112 clubs sat in the
// middle. This replaces the wall. Three letters gets you to a coast, a town or
// a club by name, which is what someone who already knows where they are going
// actually wants.
export function ClubSearch({
  value,
  onSubmit,
  onPickRegion,
}: {
  value: string;
  onSubmit: (q: string) => void;
  onPickRegion: (regionKey: string) => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState(value);
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const box = useRef<HTMLDivElement>(null);
  // Every keystroke starts a request; without this the slower of two answers
  // can land last and repopulate the list with results for an older query.
  const seq = useRef(0);

  useEffect(() => setQ(value), [value]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setItems([]);
      return;
    }
    const mine = ++seq.current;
    const timer = window.setTimeout(() => {
      void suggestPlaces({ data: { q: term } })
        .then((res) => {
          if (seq.current !== mine) return;
          setItems(res);
          setActive(-1);
        })
        .catch(() => {
          // A failed suggestion is not worth an error message: the form still
          // submits and searches server-side.
        });
    }, 160);
    return () => window.clearTimeout(timer);
  }, [q]);

  // Clicking anywhere else closes the list. Blur alone would fire before the
  // click on a suggestion registers.
  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const choose = (s: Suggestion) => {
    setOpen(false);
    if (s.kind === "club") {
      void navigate({ to: "/club/$slug", params: { slug: s.slug } });
    } else if (s.kind === "region") {
      setQ("");
      onPickRegion(s.regionKey);
    } else {
      setQ(s.town);
      onSubmit(s.town);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      choose(items[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // The second line, built here rather than on the server, so a Dutch page
  // reads "Sicilie, Italie" instead of the English the database stores.
  const subtitle = (s: Suggestion) => {
    const place = regionName(s.regionKey, s.kind === "region" ? s.label : s.regionLabel, t);
    if (s.kind === "region") return countryName(s.country, t);
    if (s.kind === "town") return place;
    return `${s.town}, ${place}`;
  };

  const KIND_LABEL: Record<Suggestion["kind"], string> = {
    region: t("sug_region"),
    town: t("sug_town"),
    club: t("sug_club"),
  };

  return (
    <div ref={box} className="relative max-w-xl">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setOpen(false);
          onSubmit(q.trim());
        }}
        className="flex flex-col gap-2 sm:flex-row"
      >
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={t("search_placeholder")}
          aria-label={t("search_placeholder")}
          role="combobox"
          aria-expanded={open && items.length > 0}
          aria-controls="club-suggestions"
          aria-autocomplete="list"
          autoComplete="off"
          className="li-display h-11 flex-1 rounded-lg border border-[#1C2430]/15 bg-white/70 px-4 text-sm text-[#1C2430] placeholder:text-[#5B6472] focus:border-[#2C4FD6] focus:outline-none"
        />
        <button
          type="submit"
          className="li-display h-11 rounded-lg border-2 border-[#1C2430] bg-[#1C2430] px-4 text-sm font-bold text-[#F2EFE9] transition-colors hover:border-[#2C4FD6] hover:bg-[#2C4FD6] active:translate-y-[1px]"
        >
          {t("search_cta")}
        </button>
      </form>

      {open && items.length > 0 ? (
        <ul
          id="club-suggestions"
          role="listbox"
          className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-[#1C2430]/15 bg-[#F2EFE9] shadow-lg"
        >
          {items.map((s, i) => (
            <li key={`${s.kind}-${s.label}-${i}`} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(s)}
                className={`flex w-full items-baseline justify-between gap-3 px-4 py-2.5 text-left transition-colors ${
                  i === active ? "bg-[#1C2430]/8" : ""
                }`}
              >
                <span className="min-w-0">
                  <span className="li-display block truncate text-sm text-[#1C2430]">
                    {s.kind === "region" ? regionName(s.regionKey, s.label, t) : s.label}
                  </span>
                  <span className="li-mono block truncate text-[11px] text-[#5B6472]">{subtitle(s)}</span>
                </span>
                <span className="li-mono shrink-0 text-[10px] uppercase tracking-widest text-[#5B6472]">
                  {KIND_LABEL[s.kind]}
                  {s.kind !== "club" ? ` ${s.count}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
